import * as React from 'react';
import {
    Alert, Box, Button, Card, CardActions, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControlLabel, IconButton, MenuItem, Snackbar, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import RedeemIcon from '@mui/icons-material/Redeem';
import SaveIcon from '@mui/icons-material/Save';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useNavigate } from 'react-router-dom';
import {
    DashboardAppSettingDto, getSubscriptionPlans, listDashboardAppSettings, putDashboardAppSetting, updateSubscriptionPlanBenefits,
} from '../Api';
import { EffectOptionDto, SubscriptionPlanAdminDto, SubscriptionPlansAdminDto } from '../models/dtos/UserPremiumAdminDto';
import { benefitView, GROUP_INFO, groupIcon } from '../components/UserSubscriptionsCard';
import SubscriptionTierChip from '../components/SubscriptionTierChip';
import SubscribersCard from '../components/SubscribersCard';
import SubscriptionStoreEventsCard from '../components/SubscriptionStoreEventsCard';

/** A benefit row being edited. */
interface BenefitDraft {
    type: 'EFFECT' | 'DAILY_COINS';
    effectId: number | null;
    dailyCoins: string;
}

function draftsOf(plan: SubscriptionPlanAdminDto): BenefitDraft[] {
    return plan.benefits.map(b => ({
        type: b.type === 'DAILY_COINS' ? 'DAILY_COINS' : 'EFFECT',
        effectId: b.effectId,
        dailyCoins: b.dailyCoins != null ? String(b.dailyCoins) : '',
    }));
}

/** The subscription transfer switches (qbet_app_settings, seeded by betup-server-api V68), read by the Api on each restore. */
const TRANSFER_KEYS = {
    enabled: 'subscription.transfer.enabled',
    cooldownEnabled: 'subscription.transfer.cooldown_enabled',
    cooldownDays: 'subscription.transfer.cooldown_days',
};

interface TransferForm {
    enabled: boolean;
    cooldownEnabled: boolean;
    cooldownDays: string;
}

/** The Api's defaults, shown while a row is missing. */
const TRANSFER_DEFAULTS: TransferForm = { enabled: true, cooldownEnabled: false, cooldownDays: '30' };

function transferFormOf(rows: DashboardAppSettingDto[]): TransferForm {
    const byKey = new Map(rows.map(r => [r.key, r]));
    const days = byKey.get(TRANSFER_KEYS.cooldownDays)?.decimalValue;
    return {
        enabled: byKey.get(TRANSFER_KEYS.enabled)?.booleanValue ?? TRANSFER_DEFAULTS.enabled,
        cooldownEnabled: byKey.get(TRANSFER_KEYS.cooldownEnabled)?.booleanValue ?? TRANSFER_DEFAULTS.cooldownEnabled,
        // Below 1 the Api uses 30.
        cooldownDays: days == null || days < 1 ? TRANSFER_DEFAULTS.cooldownDays : String(Math.trunc(days)),
    };
}

function formatPrice(price: number, currency: string): string {
    return `${currency === 'USD' ? '$' : currency + ' '}${Number(price).toFixed(2)}`;
}

/**
 * Marketing › Subscriptions (analytics SubscriptionPlanAdminService, betup-server-api docs/subscriptions.md §2): the
 * plans as read-only cards with their store products — plans aren't created or deleted here — and their benefits, edited
 * per plan in a dialog (Edit): effects of the subscription groups (Ace access, no ads, win booster, loss cashback, shop coin bonus) and the daily
 * betcoins. The app badge comes with the plan's tier. Below: the subscription transfer switches, the live subscribers by tier (each with its events) and the
 * store traffic — the app's purchases and the stores' notifications (analytics SubscriptionMonitorService).
 */
export default function SubscriptionsPage() {
    const [data, setData] = React.useState<SubscriptionPlansAdminDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [toast, setToast] = React.useState<string | null>(null);
    const navigate = useNavigate();

    const load = React.useCallback(async () => {
        setLoading(true);
        const result = await getSubscriptionPlans();
        setLoading(false);
        setData(result);
        setError(result == null ? 'Failed to load the subscription plans.' : null);
    }, []);

    React.useEffect(() => { load(); }, [load]);

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction="row" gap={1.5} alignItems="center">
                    <WorkspacePremiumIcon color="primary" fontSize="large" />
                    <Typography variant="h4">Subscriptions</Typography>
                    <Tooltip title={'Plans and their store products are fixed here; their benefits can be edited. Live subscriptions get '
                        + 'new benefits at their next payment, or right away with Apply benefits on the player\'s Premium tab.'}>
                        <InfoOutlinedIcon color="action" />
                    </Tooltip>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="outlined" startIcon={<LocalOfferOutlinedIcon />} onClick={() => navigate('/marketing/subscriptions/offers')}>
                        Subscription offers
                    </Button>
                </Stack>
                {error && <Alert severity="error">{error}</Alert>}
                {loading && data == null && <CircularProgress />}
                {data && (
                    <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-start">
                        {data.plans.map(plan => (
                            <PlanCard key={plan.id} plan={plan} options={data.effectOptions}
                                onSaved={(next, name) => { setData(next); setToast(`${name} benefits saved`); }} />
                        ))}
                    </Stack>
                )}
                <TransferSettingsCard onSaved={() => setToast('Subscription transfer settings saved')} />
                <SubscribersCard />
                <SubscriptionStoreEventsCard />
            </Stack>
            <Snackbar open={toast != null} autoHideDuration={3000} message={toast ?? ''} onClose={() => setToast(null)} />
        </Box>
    );
}

/**
 * Subscription transfers (betup-server-api docs/subscriptions.md): a store subscription restored on another BETUP
 * account moves there — the old row ends TRANSFERRED — unless switched off (the restore is then refused). The cooldown
 * lets a subscription move again only some days after its last move.
 */
function TransferSettingsCard({ onSaved }: { onSaved: () => void }) {
    const [form, setForm] = React.useState<TransferForm>(TRANSFER_DEFAULTS);
    const [saved, setSaved] = React.useState<TransferForm>(TRANSFER_DEFAULTS);
    const [error, setError] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        listDashboardAppSettings('subscription.transfer.').then(result => {
            if (result == null) {
                setError('The settings could not be loaded; the defaults are shown.');
                return;
            }
            const loaded = transferFormOf(result);
            setForm(loaded);
            setSaved(loaded);
        });
    }, []);

    const dirty = JSON.stringify(form) !== JSON.stringify(saved);

    const save = async () => {
        const days = Number(form.cooldownDays);
        if (form.cooldownDays.trim() === '' || !Number.isInteger(days) || days < 1) {
            setError('Days between moves must be a whole number, at least 1.');
            return;
        }
        setSaving(true);
        setError(null);
        type Write = [string, Parameters<typeof putDashboardAppSetting>[1]];
        const writes: Write[] = [
            [TRANSFER_KEYS.enabled, { valueType: 'BOOLEAN', booleanValue: form.enabled }],
            [TRANSFER_KEYS.cooldownEnabled, { valueType: 'BOOLEAN', booleanValue: form.cooldownEnabled }],
            [TRANSFER_KEYS.cooldownDays, { valueType: 'DECIMAL', decimalValue: days }],
        ];
        for (const [key, body] of writes) {
            const result = await putDashboardAppSetting(key, body);
            if (result.error) {
                setError(`${key}: ${result.error}`);
                setSaving(false);
                return;
            }
        }
        setSaved(form);
        setSaving(false);
        onSaved();
    };

    return (
        <Card variant="outlined" sx={{ maxWidth: 720 }}>
            <CardContent>
                <Stack gap={1}>
                    <Stack direction="row" gap={1} alignItems="center">
                        <SwapHorizIcon color="primary" />
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>Subscription transfers</Typography>
                        <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                            disabled={!dirty || saving} onClick={save}>
                            Save
                        </Button>
                    </Stack>
                    {error && <Alert severity="warning">{error}</Alert>}
                    <FormControlLabel label="Move a subscription to the account that restores it"
                        control={<Switch checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />} />
                    <FormControlLabel label="Limit how often a subscription can move" disabled={!form.enabled}
                        control={<Switch checked={form.cooldownEnabled} onChange={e => setForm({ ...form, cooldownEnabled: e.target.checked })} />} />
                    <TextField size="small" type="number" label="Days between moves" value={form.cooldownDays} sx={{ width: 200 }}
                        disabled={!form.enabled || !form.cooldownEnabled} inputProps={{ min: 1, step: 1 }}
                        onChange={e => setForm({ ...form, cooldownDays: e.target.value.replace(/[^\d]/g, '') })} />
                    <Typography variant="caption" color="text.secondary">
                        Old app versions and the app's background sync never move a subscription; a player's Restore or Subscribe does.
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
}

/** A plan, read-only: its products, grace and benefits; Edit opens the benefit editor. */
function PlanCard({ plan, options, onSaved }: {
    plan: SubscriptionPlanAdminDto;
    options: EffectOptionDto[];
    onSaved: (data: SubscriptionPlansAdminDto, name: string) => void;
}) {
    const [editing, setEditing] = React.useState(false);

    return (
        <Card variant="outlined" sx={{ flex: '1 1 340px', maxWidth: 480, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
                <Stack gap={1.5}>
                    <PlanHeader plan={plan} />
                    <Typography variant="h5">
                        {plan.products[0] ? formatPrice(plan.products[0].price, plan.products[0].currency) : '—'}
                        {plan.products[0] && <Typography component="span" variant="body2" color="text.secondary"> / {plan.products[0].periodDays} days</Typography>}
                    </Typography>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                        {plan.products.map(p => (
                            <Chip key={p.productCode} size="small" variant="outlined" icon={<PaymentsOutlinedIcon />}
                                label={`${p.productCode} · ${formatPrice(p.price, p.currency)}`} />
                        ))}
                        <Chip size="small" variant="outlined" icon={<HourglassBottomIcon />} label={`Grace ${plan.graceHours} h`} />
                    </Stack>
                    <Divider />
                    <Typography variant="subtitle2">Benefits</Typography>
                    {plan.benefits.length === 0 && <Typography variant="body2" color="text.secondary">No benefits.</Typography>}
                    {plan.benefits.map((b, i) => {
                        const view = benefitView(b, options);
                        return (
                            <Stack key={i} direction="row" gap={1.5} alignItems="flex-start">
                                <Box sx={{ color: 'primary.main', display: 'flex', width: 24, pt: 0.25 }}>{view.icon}</Box>
                                <Box>
                                    <Typography variant="body2">{view.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">{view.detail}</Typography>
                                </Box>
                            </Stack>
                        );
                    })}
                    <BadgeRow plan={plan} />
                </Stack>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setEditing(true)}>Edit</Button>
            </CardActions>
            {editing && (
                <PlanEditDialog plan={plan} options={options} onClose={() => setEditing(false)}
                    onSaved={(data, name) => { setEditing(false); onSaved(data, name); }} />
            )}
        </Card>
    );
}

function PlanHeader({ plan }: { plan: SubscriptionPlanAdminDto }) {
    return (
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <WorkspacePremiumIcon color={plan.code === 'max' ? 'warning' : 'primary'} />
            <Typography variant="h6">{plan.name}</Typography>
            <Chip size="small" label={plan.code} sx={{ fontFamily: 'monospace' }} />
            <SubscriptionTierChip tier={plan.tier} />
            {plan.active ? <Chip size="small" color="success" label="on sale" /> : <Chip size="small" label="inactive" />}
        </Stack>
    );
}

function BadgeRow({ plan }: { plan: SubscriptionPlanAdminDto }) {
    return (
        <Stack direction="row" gap={1.5} alignItems="center" sx={{ color: 'text.secondary' }}>
            <Box sx={{ color: 'primary.main', display: 'flex', width: 24 }}><WorkspacePremiumIcon fontSize="small" /></Box>
            <Typography variant="body2">{plan.tier === 'MAX' ? 'Max' : 'Plus'} badge in the app — comes with the plan's tier, not editable here</Typography>
        </Stack>
    );
}

/** The plan's benefit editor: rows replaced in order on Save (`PUT …/plans/{id}/benefits`). */
function PlanEditDialog({ plan, options, onClose, onSaved }: {
    plan: SubscriptionPlanAdminDto;
    options: EffectOptionDto[];
    onClose: () => void;
    onSaved: (data: SubscriptionPlansAdminDto, name: string) => void;
}) {
    const [drafts, setDrafts] = React.useState<BenefitDraft[]>(() => draftsOf(plan));
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const dirty = JSON.stringify(drafts) !== JSON.stringify(draftsOf(plan));
    const update = (index: number, change: Partial<BenefitDraft>) =>
        setDrafts(list => list.map((d, i) => i === index ? { ...d, ...change } : d));
    const move = (index: number, by: number) => setDrafts(list => {
        const next = [...list];
        const [row] = next.splice(index, 1);
        next.splice(index + by, 0, row);
        return next;
    });

    const save = async () => {
        setSaving(true);
        const result = await updateSubscriptionPlanBenefits(plan.id, drafts.map(d => d.type === 'DAILY_COINS'
            ? { type: 'DAILY_COINS' as const, dailyCoins: Number(d.dailyCoins) }
            : { type: 'EFFECT' as const, effectId: d.effectId }));
        setSaving(false);
        if (result.error != null || result.data == null) {
            setError(result.error ?? 'Saving failed');
            return;
        }
        onSaved(result.data, plan.name);
    };

    return (
        <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <PlanHeader plan={plan} />
                <IconButton size="small" onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack gap={1.5}>
                    <Typography variant="subtitle2">Benefits</Typography>
                    {drafts.length === 0 && <Typography variant="body2" color="text.secondary">No benefits.</Typography>}
                    {drafts.map((d, i) => {
                        const option = options.find(o => o.id === d.effectId);
                        return (
                            <Stack key={i} direction="row" gap={1} alignItems="center">
                                <Box sx={{ color: 'primary.main', display: 'flex', width: 24 }}>
                                    {d.type === 'DAILY_COINS' ? <RedeemIcon fontSize="small" /> : groupIcon(option?.group)}
                                </Box>
                                <TextField select size="small" value={d.type} sx={{ width: 150 }}
                                    onChange={e => update(i, { type: e.target.value as BenefitDraft['type'] })}>
                                    <MenuItem value="EFFECT">Effect</MenuItem>
                                    <MenuItem value="DAILY_COINS">Daily betcoins</MenuItem>
                                </TextField>
                                {d.type === 'EFFECT'
                                    ? (
                                        <TextField select size="small" value={d.effectId ?? ''} sx={{ flex: 1 }} label="Effect"
                                            onChange={e => update(i, { effectId: Number(e.target.value) })}>
                                            {options.map(o => (
                                                <MenuItem key={o.id} value={o.id}>
                                                    {GROUP_INFO[o.group]?.label ?? `Group ${o.group}`} · {o.name ?? `Effect ${o.id}`}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )
                                    : (
                                        <TextField size="small" type="number" label="Betcoins a day" value={d.dailyCoins} sx={{ flex: 1 }}
                                            inputProps={{ min: 1 }} onChange={e => update(i, { dailyCoins: e.target.value })} />
                                    )}
                                <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                                <IconButton size="small" disabled={i === drafts.length - 1} onClick={() => move(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                                <IconButton size="small" color="error" onClick={() => setDrafts(list => list.filter((_, j) => j !== i))}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        );
                    })}
                    <Box>
                        <Button size="small" startIcon={<AddIcon />}
                            onClick={() => setDrafts(list => [...list, { type: 'EFFECT', effectId: null, dailyCoins: '' }])}>
                            Add benefit
                        </Button>
                    </Box>
                    <BadgeRow plan={plan} />
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button disabled={!dirty || saving} onClick={() => { setDrafts(draftsOf(plan)); setError(null); }}>Reset</Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" disabled={!dirty || saving} onClick={save}>
                    {saving ? <CircularProgress size={18} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
